import type { AgentChatMessage as AgentChatMessageType } from "@common/schemas/domains/agentChat/agent-chat.schema";
import { liveRowFromEntry } from "@api/agentChatStream.api";
import { Markdown } from "@design-system/Markdown/Markdown";
import { AgentChatAttachmentChip } from "./AgentChatAttachmentChip";
import { AgentChatToolCallRow } from "./AgentChatToolCallRow";
import { AgentChatSources } from "./AgentChatSources";
import { FollowUpChips } from "./FollowUpChips";
import { formatAgentChatTimestamp } from "./formatAgentChatTimestamp";
import styles from "./AgentChat.module.scss";

interface Props {
    message: AgentChatMessageType;
    onFollowUpSelected: (suggestion: string) => void;
}

export const AgentChatMessage: React.FC<Props> = ({
    message,
    onFollowUpSelected,
}) => {
    if (message.role === "user") {
        return (
            <div className={styles.userTurn}>
                {message.attachments && message.attachments.length > 0 && (
                    <div className={styles.attachmentRow}>
                        {message.attachments.map((attachment) => (
                            <AgentChatAttachmentChip
                                key={attachment.financialDocumentId}
                                fileName={attachment.fileName}
                                mimeType={attachment.mimeType}
                                mode="static"
                            />
                        ))}
                    </div>
                )}
                <div className={styles.userBubble}>{message.content}</div>
                <div className={styles.messageTimestamp}>
                    {formatAgentChatTimestamp(message.createdAt)}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.assistantTurn}>
            <div className={styles.assistantBubble}>
                <Markdown>{message.content}</Markdown>

                {message.citations && message.citations.length > 0 && (
                    <AgentChatSources citations={message.citations} />
                )}

                {message.followUpSuggestions &&
                    message.followUpSuggestions.length > 0 && (
                        <FollowUpChips
                            suggestions={message.followUpSuggestions}
                            onSelect={onFollowUpSelected}
                        />
                    )}

                {message.intent?.reasoning && (
                    <details className={styles.reasoning}>
                        <summary>Show reasoning</summary>
                        <div className={styles.reasoningBody}>
                            {message.intent.reasoning}
                        </div>
                    </details>
                )}

                {message.toolTrace && message.toolTrace.length > 0 && (
                    <div className={styles.toolCallList}>
                        {message.toolTrace.map((entry, index) => (
                            <AgentChatToolCallRow
                                key={index}
                                row={liveRowFromEntry(entry, index)}
                            />
                        ))}
                    </div>
                )}
            </div>
            <div className={styles.messageTimestamp}>
                {formatAgentChatTimestamp(message.createdAt)}
            </div>
        </div>
    );
};
